import { ref } from 'vue'

/**
 * 带输入框的对话框 —— 替代 `ElMessageBox.prompt`。
 *
 * ## 为什么与 `useConfirm` 并列而不是加个参数
 *
 * `confirm` 的 resolve 是 `void`，`prompt` 的 resolve 是**用户输入的那个串**。
 * 塞进同一个函数会让返回类型变成 `void | string`，于是每个调用点都要先判一次
 * 类型才能用 —— 而 21 处 confirm 调用一个都不需要那次判断。
 *
 * ## 语义与 EP 保持一致
 *
 * 确认时 resolve 输入值、取消时 **reject**，调用方仍然写
 * `try { const v = await prompt(...) } catch { return }`。
 * 迁移时只改函数名与解构方式，不改控制流。
 */

export interface PromptOptions {
  /** 输入框的初始值。重命名类的对话框要带上当前名字，否则用户得自己重打一遍。 */
  initialValue?: string
  placeholder?: string
  confirmButtonText?: string
  cancelButtonText?: string
  /** 返回错误文案表示不合法；返回 `true` 放行。空值校验最常用。 */
  validate?: (value: string) => true | string
}

interface PendingPrompt extends PromptOptions {
  message: string
  title: string
  resolve: (value: string) => void
  reject: (reason?: unknown) => void
}

const pending = ref<PendingPrompt | null>(null)

export function usePrompt() {
  return function prompt(
    message: string,
    title = '请输入',
    options: PromptOptions = {},
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      pending.value = { message, title, resolve, reject, ...options }
    })
  }
}

/** 仅供 PromptHost 使用。 */
export function usePromptState() {
  function accept(value: string): void {
    pending.value?.resolve(value)
    pending.value = null
  }

  function dismiss(): void {
    // reject 而不是 resolve('')：空串是一个**合法的输入**，
    // 用它表示「取消」会让调用方无法区分「点了取消」与「清空后确定」。
    pending.value?.reject(new Error('cancelled'))
    pending.value = null
  }

  return { pending, accept, dismiss }
}
